import styled from '@emotion/styled'
import { memo, type ReactNode } from 'react'

import { useUiContext } from '../../code-only/hooks/index.ts'
import { HorizontalLogo } from '../../code-only/lib/horizontal-logo/horizontal-logo.tsx'
import { useTranslate } from '../../code-only/localization/index.ts'
import { mobileSideSpace } from '../../code-only/theme.ts'
import { stickyHeaderZIndex } from '../../code-only/z-indexes.tsx'
import { Chip, ProgressBar, Tooltip } from '../../components/index.ts'
import { colorTokens, spacing, Text } from '../../foundations/index.ts'

import { CompactHeaderContactButton } from './compact-header-contact-button.tsx'
import { HeaderContactButton } from './header-contact-button.tsx'
import { HeaderSeparator } from './header-separator.tsx'

export type HeaderStatus = 'locked' | 'modification'

type HeaderCustomerLayoutProps = {
  compact?: boolean | undefined
  progress?: number | undefined
  initialProgress?: number | undefined
  title?: string | undefined
  rightBlock?: ReactNode | undefined
  headerStatus?: HeaderStatus | undefined
  centerChip?: ReactNode | undefined
  languageSelector?: ReactNode | undefined
}

export const HeaderCustomerLayout = memo<HeaderCustomerLayoutProps>(function HeaderCustomerLayout({
  rightBlock,
  progress,
  title,
  compact,
  initialProgress = 0,
  headerStatus,
  centerChip,
  languageSelector,
}) {
  const screenType = useUiContext()
  const translate = useTranslate()
  const isMobile = screenType === 'mobile'

  return (
    <StyledHeaderCustomer>
      <StyledHeaderCustomerWrapper>
        <StyledHeaderCustomerContent isMobile={isMobile}>
          <StyledRow isMobile={isMobile}>
            <StyledFirstColumn>
              {title ? (
                <Text variant="subtitle">{title}</Text>
              ) : isMobile && headerStatus ? null : (
                <HorizontalLogo color={colorTokens['color-fg-base']} height={isMobile ? 17 : 21} />
              )}
              {headerStatus === 'modification' && screenType === 'mobile' ? (
                <Chip variant="warning" size="small" icon="pen-regular">
                  {translate('modification_in_progress')}
                </Chip>
              ) : headerStatus === 'locked' && screenType === 'mobile' ? (
                <Tooltip title={translate('locked_tooltip')}>
                  <Chip variant="neutral" size="small" icon="lock-regular">
                    {translate('locked')}
                  </Chip>
                </Tooltip>
              ) : undefined}
            </StyledFirstColumn>
            {headerStatus === 'modification' && !isMobile ? (
              <Chip variant="warning" size="small" icon="pen-regular">
                {translate('modification_in_progress')}
              </Chip>
            ) : headerStatus === 'locked' && !isMobile ? (
              <Tooltip title={translate('locked_tooltip')}>
                <Chip variant="neutral" size="small" icon="lock-regular">
                  {translate('locked')}
                </Chip>
              </Tooltip>
            ) : undefined}
            {centerChip}
            <StyledRightGroup>
              {languageSelector}
              {rightBlock ? rightBlock : !isMobile ? <HeaderContactButton /> : <CompactHeaderContactButton />}
            </StyledRightGroup>
          </StyledRow>
        </StyledHeaderCustomerContent>
      </StyledHeaderCustomerWrapper>

      {compact ? <HeaderSeparator /> : <ProgressBar initialProgress={initialProgress} progress={progress} />}
    </StyledHeaderCustomer>
  )
})

const StyledHeaderCustomer = styled.div`
  position: sticky;
  top: 0;
  z-index: ${stickyHeaderZIndex};
`

const StyledHeaderCustomerWrapper = styled.div`
  background-color: ${colorTokens['color-bg-base-normal']};
`

const StyledHeaderCustomerContent = styled.div<{ isMobile: boolean }>`
  ${({ isMobile }) =>
    isMobile
      ? `padding: 0 ${mobileSideSpace}`
      : `width: 100%;
        max-width: 1352px;
        margin: 0 auto;
        padding: 0 ${spacing[50]};
  `}
`

const StyledRow = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: space-between;
  padding: ${({ isMobile }) => (isMobile ? spacing[50] : spacing[40])} 0;
  align-items: center;
`

const StyledRightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing['30']};
`

const StyledFirstColumn = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing['50']};
`
